import { Ingredient, isIngredientReal } from '@cf-shared/data-model/ingredient';

/**
 * Whether a tuned copy of a meal's rows still describes the rows that meal holds NOW
 * (df-0a55eaa0a611).
 *
 * "Split & Auto-Tune" on the portion screen sizes the recipe's parts and the meal's existing
 * foods as one set, and hands back a tuned COPY of the existing foods. In Log mode that copy is
 * written back over the meal's own rows, which is what makes the saved meal match the figure it
 * was tuned to. The copy is matched to the meal by POSITION, because that is how the tuner
 * answers: row 3 of its answer is row 3 of the list it was given.
 *
 * Between the tuning starting and the save happening the app awaits several times -- every food
 * in the recipe is read from the database -- and the meal is a shared object that whatever page
 * is on screen may also be changing. So before the tuned rows are written back, they have to
 * still be a description of the same rows. This answers that, and nothing else: it does NOT ask
 * whether the tuner did a good job, and it deliberately ignores the weights, because a changed
 * weight is exactly what the tuning is FOR.
 *
 * What it compares, position by position:
 *  - the two lists are the same length;
 *  - a real food faces the same real food, named by `foodId`;
 *  - a typed-in ("macro") row faces a typed-in row.
 *
 * A row that is missing, or `null` inside the array, counts as no match: a damaged meal document
 * is a reason to leave the amounts alone, not to guess. The caller falls back to appending the
 * split parts and leaving the existing rows as they are, which is what the app did before this
 * check existed.
 */
export function tunedRowsStillMatchMeal(mealIngredients: Ingredient[], tunedRows: Ingredient[]): boolean {
  if (!Array.isArray(mealIngredients) || !Array.isArray(tunedRows)) return false;
  if (mealIngredients.length !== tunedRows.length) return false;

  for (let i = 0; i < mealIngredients.length; i++) {
    const mealRow = mealIngredients[i];
    const tunedRow = tunedRows[i];
    if (mealRow == null || tunedRow == null) return false;

    const mealRowIsReal = isIngredientReal(mealRow);
    if (mealRowIsReal !== isIngredientReal(tunedRow)) return false;

    // only a real row names a food; two typed-in rows facing each other is as much as can be
    // asked of them, since they carry no identity of their own
    if (mealRowIsReal && isIngredientReal(tunedRow) && mealRow.foodId !== tunedRow.foodId) return false;
  }

  return true;
}
